import {
    setElementOnClick,
    createDivIn,
    createParagraphIn,
    createLinkIn,
    setElementInnerText,
    setElementInnerHTML,
} from '../ui_utils/DomUtils'
import {UIAjaxWrapper} from '../network/UIAjaxWrapper'
import {UIAPIEndpoints} from '../network/UIAPIEndpoints'
import {
    Championship25ParsedData,
    Championship25Partner,
    Championship25Qualifier,
} from '../../../../shared/Championship25Types'
import {UIUtilsEjsVariables} from '../UIUtilsEjsVariables'

interface QualifierData {
    title: string
    description: string
    joinUrl: string
}

interface SectionData {
    title: string
    openByDefault: boolean
    showJoinBtns: boolean
    qualifiers: QualifierData[]
    contentId: string
    toggleId: string
}

export class UIChampionship25Controller {
    private readonly hostToggleBtn: HTMLButtonElement
    private readonly joinToggleBtn: HTMLButtonElement
    private readonly hostPanel: HTMLElement
    private readonly playerPanel: HTMLElement

    constructor() {
        this.hostToggleBtn = document.getElementById('championship25-host-toggle-btn') as HTMLButtonElement
        this.joinToggleBtn = document.getElementById('championship25-join-toggle-btn') as HTMLButtonElement
        this.hostPanel = document.getElementById('championship25-host-panel') as HTMLElement
        this.playerPanel = document.getElementById('championship25-player-panel') as HTMLElement
        setElementOnClick(this.hostToggleBtn, () => this.showHost(), true)
        setElementOnClick(this.joinToggleBtn, () => this.showPlayer(), true)

        void UIChampionship25Controller.loadContent()
    }

    private showHost() {
        this.hostToggleBtn.classList.add('active')
        this.joinToggleBtn.classList.remove('active')
        this.hostPanel.classList.remove('hidden')
        this.playerPanel.classList.add('hidden')
    }

    private showPlayer() {
        this.joinToggleBtn.classList.add('active')
        this.hostToggleBtn.classList.remove('active')
        this.playerPanel.classList.remove('hidden')
        this.hostPanel.classList.add('hidden')
    }

    private static async loadContent() {
        try {
            const response = await UIAjaxWrapper.getRequestHandler(UIAPIEndpoints.apiChampionship25Content(), undefined)
            if(response == undefined) return
            const data = response.json as Championship25ParsedData
            if(data == undefined) return
            if(Array.isArray(data.qualifiers)) {
                const sections = UIChampionship25Controller.buildSectionsFromQualifiers(data.qualifiers)
                UIChampionship25Controller.renderQualifiers(sections)
                UIChampionship25Controller.bindQualifiersToggle()
            }
            if(Array.isArray(data.partners)) {
                UIChampionship25Controller.renderPartners(data.partners)
            }
        } catch(e) {
            return
        }
    }

    private static buildSectionsFromQualifiers(qualifiers: Championship25Qualifier[]): SectionData[] {
        interface ParsedBase {title: string, location: string, joinUrl: string}
        interface ParsedWithDate extends ParsedBase {date: Date}
        interface ParsedTbd extends ParsedBase {date: ''}

        const parsed: (ParsedWithDate | ParsedTbd)[] = qualifiers.map((qualifier) => ({
            title: qualifier?.title ?? '',
            location: qualifier?.location ?? '',
            date: UIChampionship25Controller.parseDate(qualifier?.date),
            joinUrl: qualifier?.joinUrl ?? '',
        }))

        const now = new Date()
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate())
        const upcomingWithDates: ParsedWithDate[] = []
        const upcomingTbd: ParsedTbd[] = []
        const completed: ParsedWithDate[] = []

        for(const qualifier of parsed) {
            if(qualifier.date == '') {
                upcomingTbd.push(qualifier as ParsedTbd)
                continue
            }
            const date = qualifier.date as Date
            if(date < startOfToday) {
                completed.push(qualifier as ParsedWithDate)
                continue
            }
            upcomingWithDates.push(qualifier as ParsedWithDate)
        }

        upcomingWithDates.sort((a, b) => a.date.getTime() - b.date.getTime())
        completed.sort((a, b) => b.date.getTime() - a.date.getTime())

        const format = (qualifier: ParsedWithDate | ParsedTbd) => {
            const description = qualifier.date == ''
                ? `${qualifier.location} • Date TBD`
                : `${qualifier.location} • ${qualifier.date.toLocaleString('en-US', {month: 'long'})} ${qualifier.date.getDate()}`
            return {title: qualifier.title, description: description, joinUrl: qualifier.joinUrl}
        }

        const upcomingData = [
            ...upcomingWithDates.map(format),
            ...upcomingTbd.map(format),
        ]
        const completedData = completed.map(format)

        return [
            {title: 'Upcoming', openByDefault: true, showJoinBtns: true, qualifiers: upcomingData, contentId: 'championship25-qualifiers-section-upcoming', toggleId: 'championship25-qualifiers-upcoming-toggle-btn'},
            {title: 'Completed', openByDefault: false, showJoinBtns: false, qualifiers: completedData, contentId: 'championship25-qualifiers-section-completed', toggleId: 'championship25-qualifiers-completed-toggle-btn'},
        ]
    }

    private static parseDate(date: unknown): Date | '' {
        if(date == undefined) return ''

        try {
            const parsedDate = date instanceof Date ? date : new Date(date as any)
            if(isNaN(parsedDate.getTime())) return ''
            return new Date(parsedDate.getFullYear(), parsedDate.getMonth(), parsedDate.getDate())
        } catch(e) {
            return ''
        }
    }

    private static renderQualifiers(sections: SectionData[]) {
        const toggleContainer = document.getElementById('championship25-qualifiers-toggle-container') as HTMLDivElement
        const sectionsContainer = document.getElementById('championship25-qualifiers-sections-container') as HTMLDivElement
        setElementInnerHTML(toggleContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        setElementInnerHTML(sectionsContainer, {key: 'strings:colonistCommon.utils.emptyString'})

        const allToggle = createDivIn(toggleContainer, 'toggle-tab', 'championship25-qualifiers-all-toggle-btn')
        setElementInnerText(allToggle, {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'All'}})

        for(const section of sections) {
            const toggle = createDivIn(toggleContainer, 'toggle-tab' + (section.openByDefault ? ' active' : ''), section.toggleId)
            setElementInnerText(toggle, {key: 'strings:colonistCommon.utils.#buffer', options: {value: section.title}})

            const sectionDiv = createDivIn(sectionsContainer, 'qualifiers-section' + (section.openByDefault ? '' : ' hidden'), section.contentId)
            if(section.qualifiers.length == 0) {
                createParagraphIn(sectionDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'No qualifiers to show yet.'}}, '')
                continue
            }
            const content = createDivIn(sectionDiv, 'qualifiers-section-content')
            for(const qualifier of section.qualifiers) {
                const card = createDivIn(content, 'qualifier-card')
                const inner = createDivIn(card, 'qualifier-content')
                createParagraphIn(inner, {key: 'strings:colonistCommon.utils.#buffer', options: {value: qualifier.title}}, 'qualifier-title')
                createParagraphIn(inner, {key: 'strings:colonistCommon.utils.#buffer', options: {value: qualifier.description}}, '')
                if(section.showJoinBtns && qualifier.joinUrl) {
                    createLinkIn(card, qualifier.joinUrl, {key: 'strings:colonistCommon.utils.#buffer', options: {value: 'Join'}}, 'button_cta_sub', '', true)
                }
            }
        }
    }

    private static bindQualifiersToggle() {
        const allBtn = document.getElementById('championship25-qualifiers-all-toggle-btn') as HTMLDivElement
        const upcomingBtn = document.getElementById('championship25-qualifiers-upcoming-toggle-btn') as HTMLDivElement | null
        const completedBtn = document.getElementById('championship25-qualifiers-completed-toggle-btn') as HTMLDivElement | null
        const upcomingSection = document.getElementById('championship25-qualifiers-section-upcoming') as HTMLDivElement | null
        const completedSection = document.getElementById('championship25-qualifiers-section-completed') as HTMLDivElement | null

        if(upcomingBtn == null) return
        if(completedBtn == null) return
        if(upcomingSection == null) return
        if(completedSection == null) return

        setElementOnClick(allBtn, () => {
            allBtn.classList.add('active')
            upcomingBtn.classList.remove('active')
            completedBtn.classList.remove('active')
            upcomingSection.classList.remove('hidden')
            completedSection.classList.remove('hidden')
        }, true)
        setElementOnClick(upcomingBtn, () => {
            allBtn.classList.remove('active')
            upcomingBtn.classList.add('active')
            completedBtn.classList.remove('active')
            upcomingSection.classList.remove('hidden')
            completedSection.classList.add('hidden')
        }, true)
        setElementOnClick(completedBtn, () => {
            allBtn.classList.remove('active')
            upcomingBtn.classList.remove('active')
            completedBtn.classList.add('active')
            completedSection.classList.remove('hidden')
            upcomingSection.classList.add('hidden')
        }, true)
    }

    private static renderPartners(partners: Championship25Partner[]) {
        const partnersTitleElement = document.getElementById('championship25-partners-title') as HTMLElement
        const partnerElements: HTMLElement[] = []
        const viewAllPartnersBtn = document.getElementById('championship25-view-all-partners-btn') as HTMLElement
        const partnersGrid = document.getElementById('championship25-partners-grid') as HTMLDivElement

        setElementInnerText(partnersTitleElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: `Partners (${partners.length})`}})
        setElementInnerHTML(partnersGrid, {key: 'strings:colonistCommon.utils.emptyString'})

        for(const partner of partners) {
            const item = createDivIn(partnersGrid, 'partner-item')
            if(!UIUtilsEjsVariables.isUserOnDiscord && (partner.imageUrl != undefined && partner.imageUrl.length > 0)) {
                const partnerImg = document.createElement('img')
                partnerImg.src = partner.imageUrl
                partnerImg.alt = partner.name
                item.appendChild(partnerImg)
            }
            const nameDiv = createDivIn(item, 'partner-name')
            setElementInnerText(nameDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: partner.name}})
            partnerElements.push(item)
        }

        const total = partners.length
        const limit = window.innerWidth <= 991 ? 6 : 14

        if(total <= limit) {
            viewAllPartnersBtn.classList.add('hidden')
            return
        }

        for(let i = limit; i < total; i++) {
            partnerElements[i].classList.add('hidden')
        }

        setElementOnClick(viewAllPartnersBtn, () => {
            for(const item of partnerElements) item.classList.remove('hidden')
            viewAllPartnersBtn.classList.add('hidden')
        }, true)
    }
}
